import React, { useState, useCallback, useMemo } from 'react';
import { Typography, Button, Table, Tag, Space, Row, Col, message, List, Card, Grid, Input, Spin, Badge } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined, InfoCircleOutlined, BellOutlined, MessageOutlined } from '@ant-design/icons';
import { getAlerts, deleteAlert } from '@/api/alerts';
import { Alert } from '@/types/community/alert';
import { useAuth } from '@/contexts/AuthContext';
import AlertModal from '@/components/features/community/AlertModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

const severityColors: Record<Alert['type'], string> = {
  emergency: 'red',
  maintenance: 'orange',
  security: 'purple',
  weather: 'blue',
  other: 'default',
};

const severityIcons: Record<Alert['type'], React.ReactNode> = {
  emergency: <WarningOutlined style={{ color: severityColors.emergency }} />,
  maintenance: <InfoCircleOutlined style={{ color: severityColors.maintenance }} />,
  security: <BellOutlined style={{ color: severityColors.security }} />,
  weather: <InfoCircleOutlined style={{ color: severityColors.weather }} />,
  other: <InfoCircleOutlined style={{ color: severityColors.other }} />,
};

export default function EmergencyAlertsPage() {
  const { t } = useTranslation(['emergencyAlerts', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [deleteError, setDeleteError] = useState<Error | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  const { data: alertsData, isLoading, error: queryError } = useQuery({
    queryKey: ['alerts', firestoreUser?.workspace?.id],
    queryFn: () => {
      return getAlerts({ 
        firestoreUser: firestoreUser!,
        pageSize: 100 // Get all alerts for this view
      });
    },
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  

  // Mutations
  const deleteAlertMutation = useMutation({
    mutationFn: (id: string) => deleteAlert(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('emergencyAlerts:alertDeleted'));
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setIsDeleteModalVisible(false);
      setSelectedAlert(null);
      setDeleteError(undefined);
    },
    onError: (error: any) => {
      setDeleteError(error);
    }
  });

  // Memoized values
  const alertList = useMemo(() => alertsData?.alerts || [], [alertsData]);

  // Utility functions
  const handleAddAlert = useCallback(() => {
    setSelectedAlert(null);
    setIsModalOpen(true);
  }, []);

  const handleEditAlert = useCallback((alert: Alert) => {
    setSelectedAlert(alert);
    setIsModalOpen(true);
  }, []);

  const handleViewAlert = useCallback((alert: Alert) => {
    navigate(`/community/alerts/${alert.id}`, { 
      state: { 
        alert,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteAlert = useCallback((alert: Alert) => {
    setSelectedAlert(alert);
    setDeleteError(undefined);
    setIsDeleteModalVisible(true);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedAlert(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedAlert(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedAlert) {
      deleteAlertMutation.mutate(selectedAlert.id);
    }
  }, [selectedAlert, deleteAlertMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedAlert(null);
    setDeleteError(undefined);
  }, []);

  const handleErrorDismiss = useCallback(() => {
    setDeleteError(undefined);
  }, []);

  // Filter alerts
  const filteredAlerts = useMemo(() => {
    return alertList.filter(alert => 
      alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alert.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [alertList, searchTerm]);

  const columns = [
    {
      title: t('emergencyAlerts:table.title'),
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Alert) => (
        <Button type="link" onClick={() => handleViewAlert(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('emergencyAlerts:table.message'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('emergencyAlerts:table.severity'),
      dataIndex: 'type',
      key: 'type',
      render: (type: Alert['type']) => (
        <Tag color={severityColors[type]} icon={severityIcons[type]}>
          {t(`emergencyAlerts:severities.${type}` as const, type)}
        </Tag>
      ),
    },
    {
      title: t('emergencyAlerts:table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: Alert['status']) => (
        <Tag color={status === 'active' ? 'red' : status === 'resolved' ? 'green' : 'default'}>
          {t(`emergencyAlerts:statuses.${status}` as const, status)}
        </Tag>
      ),
    },
    {
      title: t('emergencyAlerts:table.timestamp'),
      dataIndex: 'startTime',
      key: 'startTime',
      render: (date: Date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a: Alert, b: Alert) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('common:comments'),
      dataIndex: 'comments',
      key: 'comments',
      render: (comments: any[] = []) => (
        <Badge count={comments.length} size="small">
          <MessageOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
        </Badge>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Alert) => (
        <Space size="middle" wrap>
          <Button icon={<EditOutlined />} onClick={() => handleEditAlert(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.edit')}
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={() => handleDeleteAlert(record)} size={screens.xs ? 'small' : 'middle'}>
            {!screens.xs && t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  const renderAlertCard = (alert: Alert) => (
    <List.Item key={alert.id}>
      <Card
        title={<Space>{severityIcons[alert.type]} <Text strong>{alert.title}</Text></Space>}
        style={{ width: '100%' }}
        actions={[
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditAlert(alert)} key="edit"> {t('common:actions.edit')}</Button>,
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteAlert(alert)} key="delete"> {t('common:actions.delete')}</Button>,
        ]}
      >
        <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: t('common:more') }}>
          {alert.description}
        </Paragraph>
        <Space direction="vertical" size="small" style={{ fontSize: '0.85em' }}>
          <Text type="secondary">{t('emergencyAlerts:table.timestamp')}: {dayjs(alert.startTime).format('YYYY-MM-DD HH:mm')}</Text>
          <Text type="secondary">{t('emergencyAlerts:table.status')}: 
            <Tag color={alert.status === 'active' ? 'red' : alert.status === 'resolved' ? 'green' : 'default'} style={{ marginLeft: 8 }}>
              {t(`emergencyAlerts:statuses.${alert.status}` as const, alert.status)}
            </Tag>
          </Text>
          <Text type="secondary">
            <MessageOutlined style={{ marginRight: 4 }} />
            {alert.comments?.length || 0} {t('common:comments')}
          </Text>
        </Space>
      </Card>
    </List.Item>
  );

  // Check authentication and workspace access
  if (!firestoreUser) {
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="danger" style={{ fontSize: '16px' }}>
            Not authenticated
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
            Please log in to view alerts
          </Text>
        </div>
      </div>
    );
  }

  if (!firestoreUser.workspace?.id) {
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="danger" style={{ fontSize: '16px' }}>
            No workspace access
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
            You don't have access to any workspace
          </Text>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <Spin size="large" />
          <Text style={{ marginTop: '16px', fontSize: '16px' }}>
            Loading alerts...
          </Text>
          <Text type="secondary" style={{ marginTop: '8px', fontSize: '14px' }}>
            Workspace: {firestoreUser?.workspace?.name || firestoreUser?.workspace?.id || 'Unknown'}
          </Text>
        </div>
      </div>
    );
  }

  // Error state
  if (queryError) {
    return (
      <div style={{ padding: screens.xs ? '16px' : '24px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Text type="danger" style={{ fontSize: '16px', marginBottom: '16px' }}>
            Error loading alerts
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '14px', marginBottom: '24px', display: 'block' }}>
            {queryError.message}
          </Text>
          
          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              marginBottom: '24px', 
              padding: '16px', 
              backgroundColor: '#fff2f0', 
              border: '1px solid #ffccc7',
              borderRadius: '6px',
              textAlign: 'left'
            }}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>Debug Information:</Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                Error Name: {queryError.name}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                Workspace ID: {firestoreUser?.workspace?.id || 'Not available'}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                User ID: {firestoreUser?.id || 'Not available'}
              </Text>
              {queryError.stack && (
                <details style={{ marginTop: '8px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '12px' }}>Stack Trace</summary>
                  <pre style={{ 
                    fontSize: '10px', 
                    backgroundColor: '#f5f5f5', 
                    padding: '8px', 
                    marginTop: '4px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {queryError.stack}
                  </pre>
                </details>
              )}
            </div>
          )}
          
          <Space direction="vertical" size="middle">
            <Button 
              type="primary" 
              onClick={() => window.location.reload()}
              loading={isLoading}
            >
              Retry
            </Button>
            <Button 
              onClick={() => queryClient.invalidateQueries({ queryKey: ['alerts'] })}
              disabled={isLoading}
            >
              Refresh Data
            </Button>
          </Space>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={16}>
        <Col flex={1}>
           <Input.Search
            placeholder={t('searchPlaceholder', 'Search alerts by title or message...')}
            onSearch={setSearchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddAlert}
            block={screens.xs}
          >
            {t('createAlert', 'Create Alert')}
          </Button>
        </Col>
      </Row>

      {screens.xs ? (
        <List
          dataSource={filteredAlerts}
          renderItem={renderAlertCard}
          grid={{ gutter: 16, column: 1 }}
        />
      ) : (
        <Table columns={columns} dataSource={filteredAlerts} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      {/* Alert Modal */}
      <AlertModal
        open={isModalOpen}
        alert={selectedAlert}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('emergencyAlerts:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={handleErrorDismiss}
      />
    </div>
  );
} 